import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {createContext, useContext} from 'react';

const TabsContext = createContext({
    selectedIndex: 0,
    onSelect: () => {},
    forceRenderTabPanel: false,
    selectedTabClassName: '',
    selectedTabPanelClassName: ''
});

const isTabPanel = child => React.isValidElement(child) && child.type && child.type.tabsRole === 'TabPanel';

const Tabs = ({
    children,
    className,
    defaultIndex = 0,
    focusTabOnClick = true,
    forceRenderTabPanel = false,
    onSelect,
    selectedIndex,
    selectedTabClassName,
    selectedTabPanelClassName,
    ...componentProps
}) => {
    const activeIndex = typeof selectedIndex === 'number' ? selectedIndex : defaultIndex;
    let panelIndex = 0;
    const enhancedChildren = React.Children.map(children, child => {
        if (!isTabPanel(child)) return child;
        const nextChild = React.cloneElement(child, {
            tabPanelIndex: panelIndex
        });
        panelIndex += 1;
        return nextChild;
    });

    return (
        <TabsContext.Provider
            value={{
                focusTabOnClick,
                forceRenderTabPanel,
                onSelect,
                selectedIndex: activeIndex,
                selectedTabClassName,
                selectedTabPanelClassName
            }}
        >
            <div
                {...componentProps}
                className={className}
                data-rttabs
            >
                {enhancedChildren}
            </div>
        </TabsContext.Provider>
    );
};

Tabs.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    defaultIndex: PropTypes.number,
    focusTabOnClick: PropTypes.bool,
    forceRenderTabPanel: PropTypes.bool,
    onSelect: PropTypes.func,
    selectedIndex: PropTypes.number,
    selectedTabClassName: PropTypes.string,
    selectedTabPanelClassName: PropTypes.string
};

Tabs.tabsRole = 'Tabs';

const TabList = ({
    children,
    className,
    ...componentProps
}) => (
    <ul
        {...componentProps}
        className={className}
        role={componentProps.role || 'tablist'}
    >
        {React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child)) return child;
            return React.cloneElement(child, {
                tabIndexNumber: index
            });
        })}
    </ul>
);

TabList.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    role: PropTypes.string
};

TabList.tabsRole = 'TabList';

class Tab extends React.Component {
    constructor (props) {
        super(props);
        this.handleClick = this.handleClick.bind(this);
    }

    handleClick (event) {
        const {disabled, onClick, tabIndexNumber} = this.props;
        const context = this.context;
        if (disabled) return;
        if (onClick) onClick(event);
        if (context.onSelect) context.onSelect(tabIndexNumber, event);
        if (!context.focusTabOnClick) event.currentTarget.blur();
    }

    render () {
        const {
            children,
            className,
            disabled,
            disabledClassName,
            onClick: _onClick,
            selectedClassName,
            tabIndex,
            tabIndexNumber,
            ...componentProps
        } = this.props;
        const context = this.context;
        const selected = context.selectedIndex === tabIndexNumber;

        return (
            <li
                {...componentProps}
                aria-disabled={disabled ? 'true' : 'false'}
                aria-selected={selected ? 'true' : 'false'}
                className={classNames(className, {
                    [selectedClassName || context.selectedTabClassName]: selected,
                    [disabledClassName]: disabled
                })}
                data-rttab
                role={componentProps.role || 'tab'}
                tabIndex={tabIndex || (selected ? '0' : null)}
                onClick={this.handleClick}
            >
                {children}
            </li>
        );
    }
}

Tab.contextType = TabsContext;

Tab.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    disabledClassName: PropTypes.string,
    onClick: PropTypes.func,
    role: PropTypes.string,
    selectedClassName: PropTypes.string,
    tabIndex: PropTypes.string,
    tabIndexNumber: PropTypes.number
};

Tab.tabsRole = 'Tab';

const TabPanel = ({
    children,
    className,
    forceRender,
    selectedClassName,
    tabPanelIndex,
    ...componentProps
}) => {
    const context = useContext(TabsContext);
    const selected = context.selectedIndex === tabPanelIndex;
    const shouldRender = forceRender || context.forceRenderTabPanel || selected;

    return (
        <div
            {...componentProps}
            className={classNames(className, {
                [selectedClassName || context.selectedTabPanelClassName]: selected
            })}
            role={componentProps.role || 'tabpanel'}
        >
            {shouldRender ? children : null}
        </div>
    );
};

TabPanel.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    forceRender: PropTypes.bool,
    role: PropTypes.string,
    selectedClassName: PropTypes.string,
    tabPanelIndex: PropTypes.number
};

TabPanel.tabsRole = 'TabPanel';

export {
    Tab,
    TabList,
    TabPanel,
    Tabs
};
